import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Home, ChevronLeft, ChevronRight, Mail, X, EyeOff, UserCog } from 'lucide-react';
import { SidebarProvider, SidebarBrand, SidebarNav } from './Sidebar';
import api from '../../lib/axios';
import GlobalSearch from '@/components/GlobalSearch';
import FooterDateBar from '@/components/FooterDateBar';
import IncomingCallOverlay from '@/components/IncomingCallOverlay';
import VoiceController, { VOICE_STATUS_SLOT_ID } from '@/components/voice/VoiceController';
import { ActionBar } from '@/components/actionbar/ActionBar';
import { useActionBar } from '@/contexts/ActionBarContext';
import { stopImpersonation, getImpersonationExpiresAt, isImpersonating } from '@/lib/impersonation';
import { emailSenderBannerVisible } from './emailSenderBanner';
// Re-export for callers that imported stopImpersonation from Layout
export { stopImpersonation };

const HIDE_BACK_ON = ['/', '/login', '/start'];

export function EmailSenderChooser({ configs, currentId, onChange }) {
  if ((configs || []).length < 2) return null;
  return (
    <select
      value={currentId || ''}
      onChange={(event) => onChange(event.target.value)}
      className="h-7 max-w-xs rounded border border-amber-300 bg-white px-2 text-xs text-amber-900"
      aria-label="Absenderadresse auswählen"
      data-testid="email-sender-select"
    >
      {(configs || []).map((config) => (
        <option
          key={config.id}
          value={config.id}
          label={`${config.sender_name} <${config.sender_email}>`}
        />
      ))}
    </select>
  );
}

// Route-Registry: Pfad → Label + ggf. übergeordneter Pfad (für Breadcrumbs)
const ROUTE_LABELS = [
  // Static routes (longer first to win specificity)
  { match: /^\/$/, label: 'Startfenster' },
  { match: /^\/start$/, label: 'Startfenster' },
  { match: /^\/billing-preview$/, label: 'Rechnungslauf-Vorschau' },
  { match: /^\/sepa$/, label: 'Zahlungsverkehr' },
  { match: /^\/payroll$/, label: 'Honorare & Gehälter' },
  { match: /^\/teacher\/payroll$/, label: 'Meine Honorarabrechnungen' },
  { match: /^\/invoices$/, label: 'Rechnungen' },
  { match: /^\/fixed-amounts$/, label: 'Festbeträge' },
  { match: /^\/prepaid-cards$/, label: '10er-Karten' },
  { match: /^\/tariffs-discounts$/, label: 'Gebührenverwaltung' },
  { match: /^\/tariffs$/, label: 'Tarife' },
  { match: /^\/discount-groups$/, label: 'Gebührenverwaltung' },
  { match: /^\/instrument-rental$/, label: 'Instrumentenvermietung' },
  { match: /^\/students$/, label: 'Schüler' },
  { match: /^\/students\/new$/, label: 'Neuer Schüler', parent: '/students' },
  { match: /^\/students\/[^/]+\/edit$/, label: 'Schüler bearbeiten', parentFrom: (p) => p.replace(/\/edit$/, '') },
  { match: /^\/students\/[^/]+$/, label: 'Schüler-Detail', parent: '/students' },
  { match: /^\/families$/, label: 'Familien' },
  { match: /^\/families\/[^/]+$/, label: 'Familien-Detail', parent: '/families' },
  { match: /^\/guardians$/, label: 'Zahlungspflichtige' },
  { match: /^\/payer-accounts$/, label: 'Zahlungspflichtige' },
  { match: /^\/correspondence$/, label: 'Korrespondenz' },
  { match: /^\/teachers$/, label: 'Lehrer' },
  { match: /^\/teachers\/[^/]+$/, label: 'Lehrer-Detail', parent: '/teachers' },
  { match: /^\/courses$/, label: 'Aktive Kurse' },
  { match: /^\/courses\/inactive$/, label: 'Inaktive Kurse', parent: '/courses' },
  { match: /^\/courses\/[^/]+\/edit$/, label: 'Kurs bearbeiten', parentFrom: (p) => p.replace(/\/edit$/, '') },
  { match: /^\/courses\/[^/]+$/, label: 'Kurs-Detail', parent: '/courses' },
  { match: /^\/online\/[^/]+$/, label: 'Onlineunterricht', parentFrom: (p) => p.replace(/^\/online\//, '/courses/') },
  { match: /^\/lessons$/, label: 'Unterrichtseinheiten' },
  { match: /^\/locations$/, label: 'Standorte & Räume' },
  { match: /^\/locations\/[^/]+$/, label: 'Standort-Detail', parent: '/locations' },
  { match: /^\/room-finder$/, label: 'Raumfinder', parent: '/locations' },
  { match: /^\/subjects$/, label: 'Fächer' },
  { match: /^\/holidays$/, label: 'Feiertage & Ferien' },
  { match: /^\/enrollments$/, label: 'Belegungen' },
  { match: /^\/trial-lessons$/, label: 'Probestunden' },
  { match: /^\/reports$/, label: 'Berichte' },
  { match: /^\/gkz-auswertung$/, label: 'GKZ-Auswertung' },
  { match: /^\/email$/, label: 'E-Mail-Versand' },
  { match: /^\/exports$/, label: 'Exporte' },
  { match: /^\/imports$/, label: 'Importe' },
  { match: /^\/settings$/, label: 'Einstellungen' },
  { match: /^\/tenants$/, label: 'Mandanten' },
  { match: /^\/users$/, label: 'Benutzer' },
  { match: /^\/portal$/, label: 'Portal' },
  { match: /^\/unterricht(sverwaltung)?$/, label: 'Unterrichtsverwaltung' },
  { match: /^\/calendar$/, label: 'Kalender' },
];

function resolvePath(pathname) {
  for (const r of ROUTE_LABELS) {
    if (r.match.test(pathname)) return r;
  }
  return null;
}

function buildBreadcrumb(pathname) {
  const crumbs = [];
  let cur = resolvePath(pathname);
  let curPath = pathname;
  while (cur) {
    crumbs.unshift({ path: curPath, label: cur.label });
    const parentPath = cur.parentFrom ? cur.parentFrom(curPath) : cur.parent;
    if (parentPath) {
      curPath = parentPath;
      cur = resolvePath(parentPath);
    } else {
      cur = null;
    }
  }
  return crumbs;
}

const ImpersonationBanner = () => {
  if (!isImpersonating()) return null;
  let current = null;
  try { current = JSON.parse(localStorage.getItem('user') || 'null'); } catch { /* ignore */ }
  const exp = getImpersonationExpiresAt();
  const minsLeft = exp ? Math.max(0, Math.ceil((exp - Date.now()) / 60000)) : null;
  return (
    <div className="sticky top-0 z-50 flex items-center gap-3 px-6 py-2 bg-brand-anthracite text-white text-sm"
         data-testid="impersonation-banner">
      <UserCog className="w-4 h-4 shrink-0" />
      <span className="flex-1">
        Sie agieren gerade als <strong>{current?.full_name || current?.username || 'anderer Benutzer'}</strong>
        {' '}(Kontowechsel durch Super-Admin
        {minsLeft != null ? <> · noch ca. {minsLeft} Min.</> : null})
      </span>
      <button onClick={() => stopImpersonation()}
              className="px-3 py-1 rounded bg-white text-brand-anthracite font-semibold text-xs hover:bg-brand-canvas"
              data-testid="stop-impersonation-btn">
        Zurück zu meinem Konto
      </button>
    </div>
  );
};

/** Pfeil wie in der App: auf dem Handy aus der Desktop-Ansicht zurück nach /m. */
export function DesktopAppBackBar() {
  const navigate = useNavigate();
  return (
    <div
      className="lg:hidden shrink-0 flex items-center px-2 py-1.5"
      style={{ background: '#34445A' }}
      data-testid="desktop-app-backbar"
    >
      <button
        type="button"
        onClick={() => navigate('/m')}
        data-testid="desktop-back-to-app"
        className="p-1.5 rounded-full text-white"
        title="Zur App"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
    </div>
  );
}

export default function Layout({ children }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const hideBack = HIDE_BACK_ON.includes(pathname);
  const crumbs = hideBack ? [] : buildBreadcrumb(pathname);
  const onlineCourseId = pathname.match(/^\/online\/([^/]+)$/)?.[1];

  // Globale Topleiste: auf jeder Seite, außer die Seite bringt bewusst eine eigene mit (z. B. Startfenster)
  const { config: abConfig } = useActionBar();
  const ownBar = !!abConfig?.ownBar;
  const pageTitle = abConfig?.title
    || resolvePath(pathname)?.label
    || (pathname === '/' || pathname === '/start' ? 'Startfenster' : 'Musikschulverwaltung');

  // --- Sender-Banner: zeigt aktuellen E-Mail-Absender für die Rolle des Users ---
  const [senderConfig, setSenderConfig] = useState(null);
  const [senderConfigs, setSenderConfigs] = useState([]);
  const [bannerHidden, setBannerHidden] = useState(
    () => localStorage.getItem('email_sender_banner_hidden') === '1'
  );
  const tenantId = typeof window !== 'undefined' ? localStorage.getItem('active_tenant_id') : null;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [aktuell, liste] = await Promise.allSettled([
        api.get('/email/configs/my-default'),
        api.get('/email/configs'),
      ]);
      if (cancelled) return;
      setSenderConfig(aktuell.status === 'fulfilled' ? (aktuell.value.data || null) : null);
      setSenderConfigs(
        liste.status === 'fulfilled'
          ? (liste.value.data || []).filter((config) => config.is_active)
          : [],
      );
    }
    if (pathname !== '/login') load();
    return () => { cancelled = true; };
  }, [pathname, tenantId]);

  // re-show whenever a custom event is dispatched (e.g. from /email page toggle)
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem('email_sender_banner_hidden');
      setBannerHidden(false);
    };
    window.addEventListener('show-sender-banner', handler);
    return () => window.removeEventListener('show-sender-banner', handler);
  }, []);

  const dismissBanner = () => {
    localStorage.setItem('email_sender_banner_hidden', '1');
    setBannerHidden(true);
  };

  const chooseSender = (configId) => {
    if (!tenantId) return;
    localStorage.setItem(`email_sender_config_id:${tenantId}`, configId);
    const config = senderConfigs.find((item) => item.id === configId);
    if (config) setSenderConfig(config);
  };

  const showBanner = emailSenderBannerVisible({ pathname, bannerHidden, senderConfig });

  const backHomeBar = !hideBack ? (
    <div
      className="shrink-0 flex items-center gap-2 px-6 py-2.5 bg-white border-b border-slate-200"
      data-testid="layout-topbar"
    >
      <button
        onClick={() => {
          if (onlineCourseId) navigate(`/courses/${onlineCourseId}`, { replace: true });
          else navigate(-1);
        }}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm text-slate-700 hover:bg-slate-100 transition-colors"
        data-testid="back-btn"
        title="Zurück"
      >
        <ArrowLeft className="w-4 h-4" />
        Zurück
      </button>

      <span className="text-slate-300 mx-1">|</span>

      <button
        onClick={() => navigate('/')}
        className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm text-slate-700 hover:bg-slate-100 transition-colors"
        data-testid="home-btn"
        title="Zum Startfenster"
      >
        <Home className="w-4 h-4" />
      </button>

      {crumbs.map((c, idx) => (
        <React.Fragment key={c.path}>
          <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
          {idx === crumbs.length - 1 ? (
            <span className="text-sm font-medium text-slate-900 px-1" data-testid={`crumb-${idx}`}>
              {c.label}
            </span>
          ) : (
            <button
              onClick={() => navigate(c.path)}
              className="text-sm text-brand-muted hover:text-brand-red hover:underline px-1"
              data-testid={`crumb-${idx}`}
            >
              {c.label}
            </button>
          )}
        </React.Fragment>
      ))}
    </div>
  ) : null;

  const chrome = (
    <>
      {!ownBar && (
        <div
          className="bg-white text-brand-text px-4 py-2 flex items-center gap-3 shrink-0 min-w-0 border-b border-brand-border"
          data-testid="global-blue-topbar"
        >
          <h1 className="text-sm font-semibold tracking-wide uppercase truncate shrink-0 max-w-[40%] min-w-0 text-brand-anthracite" data-testid="global-topbar-title">
            {pageTitle}
          </h1>
          {/* Meldungen der Sprachsteuerung. Leer nimmt der Slot keinen Platz ein. */}
          <div
            id={VOICE_STATUS_SLOT_ID}
            className="relative flex items-center min-w-0 max-w-[38rem] empty:hidden"
            data-testid="voice-status-slot"
          />
          {abConfig?.center ? (
            <div className="flex-1 min-w-0 flex items-center justify-center" data-testid="global-topbar-center">
              {abConfig.center}
            </div>
          ) : (
            <div className="flex-1 min-w-0" />
          )}
          <div className="flex items-center gap-2 shrink-0 min-w-0">
            <ActionBar variant="light" inline />
            {abConfig?.trailing || null}
          </div>
        </div>
      )}
      {backHomeBar}
    </>
  );

  return (
    <SidebarProvider>
      <div className="flex flex-col h-screen bg-brand-canvas">
        <DesktopAppBackBar />
        <GlobalSearch />
        {/* Logo (links) + Header/Zurück (rechts): gemeinsame Höhe → Logo-Unterkante = Strich unter Zurück */}
        <div className="flex shrink-0 min-w-0">
          <SidebarBrand />
          <div className="flex-1 flex flex-col min-w-0 min-h-[3rem]">
            {chrome}
          </div>
        </div>
        <div className="flex flex-1 min-h-0">
          <SidebarNav />
          <div className="flex-1 flex flex-col overflow-hidden min-w-0">
            <main className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col min-w-0">
              <ImpersonationBanner />
              {showBanner && (
                <div
                  className="sticky top-0 z-40 flex items-center gap-3 px-6 py-2 bg-amber-50 border-b border-amber-200 text-sm"
                  data-testid="email-sender-banner"
                >
                  <Mail className="w-4 h-4 text-amber-700 shrink-0" />
                  <span className="text-amber-900">
                    <span className="font-medium">Sie senden gerade als:</span>{' '}
                    <span className="font-mono" data-testid="email-sender-address">
                      {senderConfig.sender_name} &lt;{senderConfig.sender_email}&gt;
                    </span>{' '}
                    <span className="text-amber-700">· Konfig „{senderConfig.config_name}"</span>
                    {(senderConfig.role_names || []).length > 0 && (
                      <span className="ml-2 text-xs text-brand-red">
                        Rolle: {(senderConfig.role_names || []).join(', ')}
                      </span>
                    )}
                  </span>
                  <div className="flex-1" />
                  <EmailSenderChooser
                    configs={senderConfigs}
                    currentId={senderConfig.id}
                    onChange={chooseSender}
                  />
                  <button
                    onClick={dismissBanner}
                    className="inline-flex items-center gap-1 text-xs text-amber-800 hover:bg-brand-red/10 px-2 py-1 rounded"
                    data-testid="email-sender-banner-dismiss"
                    title="Banner ausblenden (für diesen Browser)"
                  >
                    <EyeOff className="w-3.5 h-3.5" />
                    Ausblenden
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              {children}
            </main>
            <FooterDateBar />
          </div>
        </div>
        <IncomingCallOverlay />
        <VoiceController />
      </div>
    </SidebarProvider>
  );
}
